import { supabase, isSupabaseConfigured } from '../config/supabase';

/**
 * Get operating hours from Supabase
 * @returns {Promise<Object>} Operating hours object
 */
export const getOperatingHours = async () => {
  if (!isSupabaseConfigured() || !supabase) {
    console.warn('Supabase not configured');
    return { data: null, error: { message: 'Supabase not configured' } };
  }

  try {
    const { data, error } = await supabase
      .from('operating_hours')
      .select('*')
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      console.error('Error fetching operating hours:', error);
      return { data: null, error };
    }

    return { data: data || null, error: null };
  } catch (error) {
    console.error('Error in getOperatingHours:', error);
    return { data: null, error };
  }
};

/**
 * Save or update operating hours
 * @param {Object} formData - Operating hours data
 * @param {boolean} isOpen - Restaurant status (open/closed)
 * @param {Object} hours - Hours for each day
 * @param {Array} holidays - Array of holidays
 * @returns {Promise<Object>} Saved operating hours
 */
export const saveOperatingHours = async (isOpen, hours, holidays = []) => {
  if (!isSupabaseConfigured() || !supabase) {
    console.warn('Supabase not configured');
    return { data: null, error: { message: 'Supabase not configured' } };
  }

  try {
    console.log('Starting to save operating hours to Supabase...');

    // Prepare data for database
    const operatingHoursData = {
      is_open: isOpen,
      operating_hours: hours, // JSONB object with all days
      holidays: holidays || [] // JSONB array of holidays
    };

    console.log('Data to save:', operatingHoursData);

    // Check if operating hours already exists
    const { data: existing, error: checkError } = await supabase
      .from('operating_hours')
      .select('id')
      .limit(1)
      .single();

    let result;
    let isUpdate = false;

    // If no existing record or error (PGRST116 means no rows), insert new
    if (!existing || checkError?.code === 'PGRST116') {
      console.log('No existing record found, inserting new operating hours...');
      // Insert new record
      const { data, error } = await supabase
        .from('operating_hours')
        .insert([operatingHoursData])
        .select()
        .single();

      if (error) {
        console.error('Error inserting operating hours:', error);
        throw error;
      }
      result = data;
      console.log('✅ Operating hours inserted successfully:', result);
    } else {
      console.log('Existing record found, updating operating hours...', existing.id);
      isUpdate = true;
      // Update existing record
      const { data, error } = await supabase
        .from('operating_hours')
        .update(operatingHoursData)
        .eq('id', existing.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating operating hours:', error);
        throw error;
      }
      result = data;
      console.log('✅ Operating hours updated successfully:', result);
    }

    // Verify the data was saved by fetching it back
    const { data: verifyData, error: verifyError } = await supabase
      .from('operating_hours')
      .select('*')
      .eq('id', result.id)
      .single();

    if (verifyError) {
      console.warn('Could not verify saved data:', verifyError);
    } else {
      console.log('✅ Verified: Data saved correctly in Supabase:', verifyData);
    }

    return { data: result, error: null };
  } catch (error) {
    console.error('❌ Error in saveOperatingHours:', error);
    console.error('Error details:', {
      message: error.message,
      code: error.code,
      details: error.details,
      hint: error.hint
    });
    return { data: null, error };
  }
};

